import { AxiosProgressEvent } from 'axios';
import AliOSS from 'ali-oss';
import request, { MethodEnum } from '@/utils/fetch';

/** enums */
export enum UploadTypeEnum {
  video = 'videos',
  photo = 'photos',
}

export enum UploadModeEnum {
  form = 'form',
  aliyunOss = 'aliyun_oss',
}

/** interfaces && types */
export type AliOSSUploadConfig = {
  access_key_id: string,
  access_key_secret: string,
  security_token: string,
  upload_bucket: string,
  upload_host: string,
  upload_file_path: string,
  object_id: string,
  cmt_token: string,
  vid: string;
}

export type AliOSSCheckPoint = AliOSS.Checkpoint;

export interface IUploadPhotoReq {
  file: File;
  controller?: AbortController,
  onUploadProgressCb?: (percent: number)=>void
}


interface IInitVideoUploadReq {
  workType: UploadTypeEnum;
  controller: AbortController,
}

export interface IInitVideoUploadRes {
  item: AliOSSUploadConfig;
}

export interface IUploadToBackend {
  configs: AliOSSUploadConfig;
  file: File;
  controller: AbortController;
}

/** init token */
export const initVideoUploadService = ({
  workType,
  controller,
}: IInitVideoUploadReq):Promise<AliOSSUploadConfig> => {
  return request({
    url: `/uploads/${workType}/init`,
    method: MethodEnum.POST,
    signal: controller.signal,
  }).then((data) => data?.item || '').catch((e) => Promise.reject(e));
};

/** 上传视频（阿里云 OSS） */
export const uploadToOSSService = (
  configs: AliOSSUploadConfig,
  file: File,
  options: AliOSS.MultipartUploadOptions,
) => {
  const client = new AliOSS({
    accessKeyId: configs.access_key_id,
    accessKeySecret: configs.access_key_secret,
    bucket: configs.upload_bucket,
    endpoint: configs.upload_host,
    stsToken: configs.security_token,
    timeout: '3600s',
  });

  let abort = (cpt: AliOSS.Checkpoint) => {
    if (client && cpt) {
      client.abortMultipartUpload(cpt.name, cpt.uploadId).catch((e: any) => {
        console.log('---abortMultipartUpload', e);
      });
    }
  };

  return {
    task: client.multipartUpload(configs.upload_file_path, file, options).finally(() => {
      abort = () => true;
    }),
    abort: (cpt: AliOSS.Checkpoint) => {
      abort(cpt);
    },
  };
};

export const uploadVideoToBackend = ({
  configs,
  file,
  controller,
}: IUploadToBackend) => {
  return request({
    url: '/uploads/videos/save',
    method: MethodEnum.POST,
    signal: controller.signal,
    params: {
      cmt_token: configs.cmt_token,
      vid: configs.vid,
      file_size: file.size,
    },
  }).then(() => {}).catch((e) => Promise.reject(e));
};

/** 上传图片 */
export const uploadPhotoService = ({
  file,
  controller,
  onUploadProgressCb,
}: IUploadPhotoReq): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return request({
    url: '/forum/files/upload',
    method: MethodEnum.POST,
    params: formData,
    signal: controller?.signal,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progress: AxiosProgressEvent) => {
      const { loaded, total } = progress;
      if (total && typeof onUploadProgressCb === 'function') {
        onUploadProgressCb(Number(loaded/total));
      }
    },
  }).then((data) => {
    return data?.item?.url;
  }).catch((e) => Promise.reject(e));
};

/** utils */
export const getPercent = (percent: number) => `${parseFloat(
  ((percent === 1 ? 0.999 : percent) * 100).toFixed(1),
)}%`;

export const getFormattedDuration = (seconds?: number) => {
  if (!seconds) {
    return '00:00';
  }

  let time = seconds;
  const min = Math.floor(seconds / 60) >= 10 ? Math.floor(seconds / 60).toString() : '0' + Math.floor(seconds / 60);
  time -= 60 * Number.parseInt(min);
  const sec = time >= 10 ? time.toString() : '0' + time;
  return `${min}:${sec}`;
};
